interface ViewportEventTarget {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

interface VisualViewportSource extends ViewportEventTarget {
  height: number;
  offsetTop: number;
}

interface BrowserViewportSource extends ViewportEventTarget {
  innerHeight: number;
  visualViewport?: VisualViewportSource | null;
  requestAnimationFrame(callback: FrameRequestCallback): number;
  cancelAnimationFrame(handle: number): void;
}

const KEYBOARD_HEIGHT_DELTA = 120;

export function syncVisualViewport(root: HTMLElement, browser: Pick<BrowserViewportSource, 'innerHeight' | 'visualViewport'>): void {
  const viewport = browser.visualViewport;
  const height = Math.max(1, Math.round(viewport?.height || browser.innerHeight));
  const offsetTop = Math.max(0, Math.round(viewport?.offsetTop || 0));
  const keyboardOpen = height + KEYBOARD_HEIGHT_DELTA < browser.innerHeight;

  root.style.setProperty('--app-viewport-height', `${height}px`);
  root.style.setProperty('--app-viewport-offset-top', `${offsetTop}px`);
  root.dataset.virtualKeyboard = keyboardOpen ? 'open' : 'closed';
}

export function installVisualViewportSync(
  root: HTMLElement = document.documentElement,
  browser: BrowserViewportSource = window
): () => void {
  const viewport = browser.visualViewport;
  let animationFrame = 0;

  const update = () => {
    if (animationFrame) browser.cancelAnimationFrame(animationFrame);
    animationFrame = browser.requestAnimationFrame(() => {
      animationFrame = 0;
      syncVisualViewport(root, browser);
    });
  };

  const listener: EventListener = () => update();
  browser.addEventListener('resize', listener);
  browser.addEventListener('orientationchange', listener);
  viewport?.addEventListener('resize', listener);
  viewport?.addEventListener('scroll', listener);
  syncVisualViewport(root, browser);

  return () => {
    browser.removeEventListener('resize', listener);
    browser.removeEventListener('orientationchange', listener);
    viewport?.removeEventListener('resize', listener);
    viewport?.removeEventListener('scroll', listener);
    if (animationFrame) browser.cancelAnimationFrame(animationFrame);
  };
}
